import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { EssayContent, saveEssayContent } from '../../hooks/useEssayApi';
import { ApiError } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { useDocumentAutosave, type DocumentAutosave, type SaveState } from './useDocumentAutosave';

export type { SaveState };
export type EssayAutosave = DocumentAutosave<EssayContent> & {
  /** Эссе изменили на сервере, пока редактор был открыт: сохранять поверх нельзя. */
  isConflict: boolean;
  /** Перечитать эссе с сервера: редактор пересоберётся на свежей версии. */
  reload: () => void;
};

function normalize(c: Partial<EssayContent>): EssayContent {
  return {
    done: c.done ?? [],
    issues: c.issues ?? [],
    plans: c.plans ?? [],
    interesting: c.interesting ?? [],
  };
}

/**
 * Автосохранение месячного эссе. Вся таймерная механика живёт в
 * `useDocumentAutosave` — здесь остаётся то, что специфично для эссе: форма
 * содержимого из четырёх разделов, эндпоинт и тексты ошибок.
 */
export function useEssayAutosave(
  essayId: string,
  initialContent: Partial<EssayContent>,
  readOnly?: boolean,
  importedAt?: string | null,
  updatedAt?: string,
): EssayAutosave {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Предсказуемые отказы: 403 — эссе уже сдано, 409 — его заменил импорт, пока
  // редактор был открыт. Текст сервера не показываем: он всегда русский.
  const errorMessage = useCallback(
    (error: unknown) =>
      error instanceof ApiError && error.status === 403
        ? t('essays.submittedNotice')
        : error instanceof ApiError && error.status === 409
          ? (error.response.data as { code?: string } | null)?.code === 'essay_imported'
            ? t('essays.importConflict')
            : t('essays.changedConflict')
          : t('essays.saveFailedMessage'),
    [t],
  );

  const initial = useMemo(
    () => normalize(initialContent),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [essayId],
  );

  // Фиксируем на открытии, как и содержимое: после фонового перезапроса проп
  // уже покажет импорт, а текст в редакторе останется старым.
  const knownImportedAt = useMemo(
    () => importedAt ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [essayId],
  );

  // Версия, на которой стоит редактор: с открытия, дальше — из ответа на
  // каждое своё сохранение.
  const versionRef = useRef(updatedAt);
  // Сохранения идут строго по очереди: параллельный PUT ушёл бы со старой
  // версией и получил бы ложный 409.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  const [isConflict, setIsConflict] = useState(false);
  // Ответ 409: после него сохранять поверх бессмысленно — каждый PUT обречён,
  // а досылка при «Обновить эссе» затёрла бы чужую правку или пугала тостом.
  const conflictRef = useRef<unknown>(null);

  // Инвалидация после каждого сохранения — то, что раньше делал onSuccess
  // мутации: без неё список эссе и статус остаются с прежними датами.
  const save = useCallback(
    (id: string, content: EssayContent) => {
      const run = queueRef.current
        .catch(() => undefined)
        .then(async () => {
          if (conflictRef.current) throw conflictRef.current;
          try {
            const saved = await saveEssayContent(id, content, knownImportedAt, versionRef.current);
            versionRef.current = saved.updatedAt;
            queryClient.invalidateQueries({ queryKey: ['essays', id] });
            queryClient.invalidateQueries({ queryKey: ['essays', 'mine'] });
            return saved;
          } catch (error) {
            if (error instanceof ApiError && error.status === 409) {
              conflictRef.current = error;
              setIsConflict(true);
            }
            throw error;
          }
        });
      queueRef.current = run;
      return run;
    },
    [queryClient, knownImportedAt],
  );

  // Конфликт относится к одной версии одного эссе: после перечитывания или
  // смены эссе редактор может не перемонтироваться — сбрасываем здесь.
  const clearConflict = useCallback(() => {
    conflictRef.current = null;
    setIsConflict(false);
  }, []);
  useEffect(clearConflict, [essayId, clearConflict]);

  const reload = useCallback(() => {
    clearConflict();
    void queryClient.resetQueries({ queryKey: ['essays', essayId] });
  }, [queryClient, essayId, clearConflict]);

  const autosave = useDocumentAutosave<EssayContent>({
    docId: essayId,
    initial,
    readOnly,
    save,
    errorMessage,
    onDetachedSuccess: () => queryClient.invalidateQueries({ queryKey: ['essays'] }),
    onDetachedFailure: (error) => {
      // Про конфликт человек уже знает — плашка в редакторе.
      if (conflictRef.current) return;
      showToast({ title: t('essays.saveError'), message: errorMessage(error), color: 'red' });
    },
  });
  // После конфликта правки в редакторе не сохранены и уже не сохранятся: закрытие
  // или перезагрузка вкладки молча их потеряли бы — спрашиваем браузерным диалогом.
  // ponytail: переходы внутри SPA не перехватываем — BrowserRouter не умеет useBlocker,
  // нужен переход на data router.
  useEffect(() => {
    if (!isConflict) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isConflict]);

  return { ...autosave, isConflict, reload };
}
